/**
 * Swoop score bar — race to N (default 500). Lower is better, so
 * progress bars are colored to indicate "danger" the closer they get to
 * the threshold. Your own row is highlighted in green.
 */

import { useState } from "react";
import type { SwoopPublicState } from "../../../../shared/games/swoop.js";
import { useWindowWidth } from "../../lib/uiHooks.js";

const SEAT_COLORS = [
  "#5ec8e2", // cyan
  "#f08b5b", // orange
  "#9d6bd6", // purple
  "#e3c95b", // gold
  "#9bc977", // olive
  "#e07a7a", // coral
  "#7ec9b6", // teal
  "#cf8ec9", // pink
];

// Phase labels — we only surface non-play phases. The "Play" label was
// redundant clutter at the top-right (the active board makes it obvious),
// so we render an empty string for play.
const PHASE_LABEL: Record<string, string> = {
  play: "",
  round_end: "Round over",
  match_end: "Match over",
};

export function SwoopScoreBar({
  state,
  yourSeat,
}: {
  state: SwoopPublicState;
  yourSeat: number | null;
}) {
  // Default collapsed on small viewports — the score bar covered too much
  // of the table on phones. Desktop keeps it open by default.
  const windowWidth = useWindowWidth();
  const [open, setOpen] = useState(() => windowWidth > 700);

  function colorFor(seat: number): string {
    if (yourSeat !== null && seat === yourSeat) return "var(--my-team)";
    return SEAT_COLORS[seat % SEAT_COLORS.length]!;
  }

  if (!open) {
    // Collapsed pill — small, unobtrusive, click to expand.
    return (
      <button
        className="swoop-score-collapsed"
        onClick={() => setOpen(true)}
        title="Show scoreboard"
        aria-label="Show scoreboard"
      >
        Round {state.roundNumber} · scores
      </button>
    );
  }

  return (
    <div className="score-bar swoop-score-bar" role="img" aria-label="Swoop score">
      <div className="score-bar-header">
        <div className="score-bar-side defenders">
          <span className="role-label">ROUND {state.roundNumber}</span>
          {(PHASE_LABEL[state.phase] ?? state.phase) && (
            <span className="rank-label">{PHASE_LABEL[state.phase] ?? state.phase}</span>
          )}
        </div>
        <div className="score-bar-center">
          <span className="muted">
            {state.altScoring ? "alt scoring" : "standard scoring"} ·
            {" "}lowest wins
          </span>
        </div>
        <div className="score-bar-side attackers">
          <span className="role-label">DANGER {state.matchTarget}</span>
        </div>
        <button
          className="score-collapse-inline"
          onClick={() => setOpen(false)}
          title="Hide scoreboard"
          aria-label="Hide scoreboard"
        >
          ×
        </button>
      </div>

      <div className="euchre-race-wrap">
        {state.players.map((p) => {
          const pct = Math.min(100, (p.totalScore / state.matchTarget) * 100);
          return (
            <div className="euchre-race-row" key={p.seat}>
              <span
                className="euchre-race-label"
                style={{ color: colorFor(p.seat), minWidth: 90, textAlign: "right" }}
                title={p.displayName}
              >
                {p.displayName}
              </span>
              <div className="euchre-race-track">
                <div
                  className="euchre-race-fill"
                  style={{
                    width: `${pct}%`,
                    background: colorFor(p.seat),
                  }}
                />
                {[100, 200, 300, 400].map((m) => (
                  <div
                    key={m}
                    className="euchre-race-tick"
                    style={{ left: `${(m / state.matchTarget) * 100}%` }}
                  />
                ))}
                <div className="euchre-race-value">
                  {p.totalScore} / {state.matchTarget}
                  {p.isOut && (
                    <span className="muted" style={{ marginLeft: 8, fontWeight: 400 }}>
                      · OUT
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
